import { useEffect, useMemo, useRef, useState } from 'react';
import { matchPath, useLocation } from 'react-router-dom';
import { useApolloClient } from '@apollo/client';

import {
  SpecialityGroupListDocument,
  type SpecialityGroupListQuery,
  useSpecialityGroupListLazyQuery,
} from '@/graphql';

import { trackEvent, trackPageView } from './ga4';

const CME_FOLDER_NAME = 'Aktuelle CME-Fortbildungen';

const normalizePathname = (pathname: string): string => {
  const normalized = pathname.replace(/\/+$/, '');
  return normalized.length ? normalized : '/';
};

const getPageLocation = (): string | undefined => {
  if (typeof window === 'undefined') return undefined;
  return window.location?.href;
};

const getStandardParams = (pagePath: string): Record<string, unknown> => {
  const pageLocation = getPageLocation();
  return {
    page_path: pagePath,
    ...(pageLocation ? { page_location: pageLocation } : {}),
  };
};

const getCmeFolderIdFromList = (
  data: SpecialityGroupListQuery | null | undefined
): string | null => {
  const list = data?.userSpecialityGroupList ?? [];
  for (const item of list) {
    const group = item?.specialityGroup;
    if (group?.name === CME_FOLDER_NAME) return String(group.id);
  }
  return null;
};

const useCmeFolderId = (enabled: boolean): string | null => {
  const apolloClient = useApolloClient();
  const [cmeFolderId, setCmeFolderId] = useState<string | null>(null);
  const requestedRef = useRef(false);

  const [loadSpecialityGroups, { data: listData }] = useSpecialityGroupListLazyQuery({
    fetchPolicy: 'cache-first',
  });

  useEffect(() => {
    if (!enabled || cmeFolderId) return;

    try {
      const cached = apolloClient.readQuery<SpecialityGroupListQuery>({
        query: SpecialityGroupListDocument,
      });
      const fromCache = getCmeFolderIdFromList(cached);
      if (fromCache) {
        setCmeFolderId(fromCache);
        return;
      }
    } catch {
      // Cache miss; fall through to best-effort fetch.
    }

    if (!requestedRef.current) {
      requestedRef.current = true;
      loadSpecialityGroups();
    }
  }, [apolloClient, cmeFolderId, enabled, loadSpecialityGroups]);

  useEffect(() => {
    if (!enabled || cmeFolderId) return;
    const resolved = getCmeFolderIdFromList(listData);
    if (resolved) setCmeFolderId(resolved);
  }, [cmeFolderId, enabled, listData]);

  return cmeFolderId;
};

export const useGa4RouteTracking = (): void => {
  const location = useLocation();

  const pagePath = useMemo(
    () => `${location.pathname}${location.search}`,
    [location.pathname, location.search]
  );

  const normalizedPathname = useMemo(
    () => normalizePathname(location.pathname),
    [location.pathname]
  );

  const networkMatch = useMemo(
    () =>
      matchPath(
        { path: '/network-sessions-by-spec/:specialityGroupId', end: true },
        normalizedPathname
      ),
    [normalizedPathname]
  );

  const specialityGroupId = networkMatch?.params?.specialityGroupId ?? null;
  const cmeFolderId = useCmeFolderId(Boolean(networkMatch));

  const lastNavigationKeyRef = useRef<string | null>(null);
  const lastCmeFireRef = useRef<string | null>(null);

  useEffect(() => {
    const navKey = location.key && location.key !== 'default' ? location.key : pagePath;
    if (lastNavigationKeyRef.current === navKey) return;
    lastNavigationKeyRef.current = navKey;

    trackPageView(pagePath);

    if (matchPath({ path: '/biblio', end: true }, normalizedPathname)) {
      trackEvent('library_viewed', getStandardParams(pagePath));
    }

    // Must match exactly `/subscription` (not `/subscription/checkout`).
    if (matchPath({ path: '/subscription', end: true }, normalizedPathname)) {
      trackEvent('checkout_viewed', getStandardParams(pagePath));
    }

    // Reset CME fire guard on each navigation.
    lastCmeFireRef.current = null;
  }, [location.key, normalizedPathname, pagePath]);

  useEffect(() => {
    if (!specialityGroupId || !cmeFolderId) return;
    if (specialityGroupId !== cmeFolderId) return;
    if (lastCmeFireRef.current === pagePath) return;
    lastCmeFireRef.current = pagePath;

    trackEvent('cme_folder_viewed', {
      folder_id: specialityGroupId,
      folder_name: CME_FOLDER_NAME,
      ...getStandardParams(pagePath),
    });
  }, [cmeFolderId, pagePath, specialityGroupId]);
};
